<script setup lang="ts">
    import { computed, reactive, watch } from 'vue';
    import type { HipSpineSafeZone } from '@/domain/anatomy/pelvis/hipSpineNomogramCalculations';
    import {
        MAX_ANTEVERSION,
        MAX_INCLINATION,
        MIN_ANTEVERSION,
        MIN_INCLINATION,
    } from '@/domain/anatomy/pelvis/anteversionInclination';

    const props = defineProps<{
        bounds: HipSpineSafeZone;
        inclinationLowerMax: number;
        inclinationUpperMin: number;
        anteversionLowerMax: number;
        anteversionUpperMin: number;
    }>();

    const emit = defineEmits<{
        'update:bound': [field: keyof HipSpineSafeZone, value: number];
        close: [];
    }>();

    // Axis minimums from the nomogram chart — substituted on commit when
    // the field is empty. The view model's clampBound is the authoritative
    // range backstop; these constants only seed the display.
    const AXIS_MIN: Record<keyof HipSpineSafeZone, number> = {
        minInclination: MIN_INCLINATION,
        maxInclination: MIN_INCLINATION,
        minAnteversion: MIN_ANTEVERSION,
        maxAnteversion: MIN_ANTEVERSION,
    };

    const ranges = computed<Record<keyof HipSpineSafeZone, { min: number; max: number }>>(() => ({
        minInclination: { min: MIN_INCLINATION, max: props.inclinationLowerMax },
        maxInclination: { min: props.inclinationUpperMin, max: MAX_INCLINATION },
        minAnteversion: { min: MIN_ANTEVERSION, max: props.anteversionLowerMax },
        maxAnteversion: { min: props.anteversionUpperMin, max: MAX_ANTEVERSION },
    }));

    function isInRange(field: keyof HipSpineSafeZone, value: number | null): value is number {
        if (value == null || Number.isNaN(value)) return false;
        const { min, max } = ranges.value[field];
        return value >= min && value <= max;
    }

    // Local buffer lets the user type values that are temporarily out of
    // range (including empty) without being snapped back by the parent's
    // clamp. In-range keystrokes emit live so the nomogram previews
    // immediately; out-of-range values are buffered until blur/Enter.
    const buffer = reactive<Record<keyof HipSpineSafeZone, number | null>>({
        minInclination: props.bounds.minInclination,
        maxInclination: props.bounds.maxInclination,
        minAnteversion: props.bounds.minAnteversion,
        maxAnteversion: props.bounds.maxAnteversion,
    });

    watch(
        () => props.bounds,
        (next) => {
            buffer.minInclination = next.minInclination;
            buffer.maxInclination = next.maxInclination;
            buffer.minAnteversion = next.minAnteversion;
            buffer.maxAnteversion = next.maxAnteversion;
        },
        { deep: true },
    );

    function onInput(field: keyof HipSpineSafeZone, value: number | null) {
        buffer[field] = value;
        if (isInRange(field, value)) emit('update:bound', field, value);
    }

    function commit(field: keyof HipSpineSafeZone) {
        const raw = buffer[field];
        if (isInRange(field, raw)) {
            // Already emitted live during typing. Re-sync in case a
            // dynamic constraint caused the parent's clamp to move the
            // prop away from what we emitted.
            buffer[field] = props.bounds[field];
            return;
        }
        const value = raw == null || Number.isNaN(raw) ? AXIS_MIN[field] : raw;
        emit('update:bound', field, value);
        // Re-sync from the parent's post-clamp value. Handles the no-op
        // case (empty buffer when prop already equals AXIS_MIN) where the
        // prop watcher would not fire, and avoids a one-frame flicker of
        // the pre-clamp typed value.
        buffer[field] = props.bounds[field];
    }
</script>

<template>
    <div class="surgeon-defined-bounds pa-2 border-sm rounded-lg">
        <div class="d-flex align-center mb-4">
            <span class="text-subtitle-1 font-weight flex-grow-1 text-center">Surgeon Defined Bounds</span>
            <v-hover v-slot="{ isHovering, props: hoverProps }">
                <v-btn
                    v-bind="hoverProps"
                    elevation="0"
                    icon
                    size="x-small"
                    height="35"
                    width="35"
                    :base-color="isHovering ? 'white' : 'surface-light'"
                    @click="emit('close')"
                >
                    <v-icon size="20" color="dark-blue">mdi-close</v-icon>
                </v-btn>
            </v-hover>
        </div>

        <v-row dense class="mb-2">
            <v-col cols="6">
                <app-number-input
                    :model-value="buffer.minInclination"
                    class="bound-input"
                    :step="1"
                    density="compact"
                    hide-details
                    @update:model-value="onInput('minInclination', $event)"
                    @blur="commit('minInclination')"
                    @keydown.enter="commit('minInclination')"
                />
                <div class="text-caption text-center mt-1">Inclination lower bound</div>
            </v-col>
            <v-col cols="6">
                <app-number-input
                    :model-value="buffer.maxInclination"
                    class="bound-input"
                    :step="1"
                    density="compact"
                    hide-details
                    @update:model-value="onInput('maxInclination', $event)"
                    @blur="commit('maxInclination')"
                    @keydown.enter="commit('maxInclination')"
                />
                <div class="text-caption text-center mt-1">Inclination upper bound</div>
            </v-col>
        </v-row>

        <v-row dense>
            <v-col cols="6">
                <app-number-input
                    :model-value="buffer.minAnteversion"
                    class="bound-input"
                    :step="1"
                    density="compact"
                    hide-details
                    @update:model-value="onInput('minAnteversion', $event)"
                    @blur="commit('minAnteversion')"
                    @keydown.enter="commit('minAnteversion')"
                />
                <div class="text-caption text-center mt-1">Anteversion lower bound</div>
            </v-col>
            <v-col cols="6">
                <app-number-input
                    :model-value="buffer.maxAnteversion"
                    class="bound-input"
                    :step="1"
                    density="compact"
                    hide-details
                    @update:model-value="onInput('maxAnteversion', $event)"
                    @blur="commit('maxAnteversion')"
                    @keydown.enter="commit('maxAnteversion')"
                />
                <div class="text-caption text-center mt-1">Anteversion upper bound</div>
            </v-col>
        </v-row>
    </div>
</template>

<style scoped lang="scss">
    .bound-input {
        max-width: 80px;
        margin: 0 auto;
    }

    .surgeon-defined-bounds :deep(.v-field__outline) {
        color: rgba(var(--v-border-color), var(--v-border-opacity));
    }
</style>
